/**
 * Read the CURRENT user's per-user mute flag for a unified conversation from
 * whatever the inbox query cache already holds — the detail entry first, then
 * any cached list page. Never fetches ({@link resolveIsMuted} does).
 *
 * `undefined` when no cached row knows (inbox never opened, row not on a loaded
 * page, or cached before `isMuted` existed). Callers must treat that as "not
 * muted" so an unknown state fails open and the user is still notified.
 */
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { inboxApi } from '../api/inbox.api';
import { inboxKeys } from '../api/inbox.keys';
import type { IListUnifiedResponse, IUnifiedConversation } from '../types';

export function readCachedIsMuted(
    queryClient: QueryClient,
    unifiedConversationId: string,
): boolean | undefined {
    const detail = queryClient.getQueryData<IUnifiedConversation>(
        inboxKeys.unifiedDetail(unifiedConversationId),
    );
    if (detail?.isMuted !== undefined) return detail.isMuted;

    const lists = queryClient.getQueriesData<InfiniteData<IListUnifiedResponse>>({
        queryKey: [...inboxKeys.unified(), 'list'],
    });
    for (const [, data] of lists) {
        if (!data || !Array.isArray(data.pages)) continue;
        for (const page of data.pages) {
            const row = page.data.find((r) => r.id === unifiedConversationId);
            if (row?.isMuted !== undefined) return row.isMuted;
        }
    }
    return undefined;
}

/** Mutes change only by this user's own action, which invalidates the list. */
const MUTED_IDS_STALE_MS = 5 * 60_000;

/**
 * Like {@link readCachedIsMuted}, but when no cached row knows it asks the
 * server once for the user's muted ids (cached, shared by every caller). That
 * covers a conversation the inbox has not loaded — e.g. while the user is on
 * another page. A failed fetch reads as "not muted", so the user is still told.
 */
export async function resolveIsMuted(
    queryClient: QueryClient,
    unifiedConversationId: string,
): Promise<boolean> {
    const cached = readCachedIsMuted(queryClient, unifiedConversationId);
    if (cached !== undefined) return cached;
    try {
        const mutedIds = await queryClient.fetchQuery({
            queryKey: inboxKeys.mutedConversationIds(),
            queryFn: () => inboxApi.listMutedConversationIds(),
            staleTime: MUTED_IDS_STALE_MS,
        });
        return mutedIds.includes(unifiedConversationId);
    } catch {
        return false;
    }
}
